import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Eye, EyeOff, Mail, Send, Plug } from "lucide-react";
import { toast } from "react-toastify";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import HelpTooltipIcon from "../HelpTooltipIcon";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      ...(theme.scrollbarStyles || {}),
    },

    sectionCard: {
      borderRadius: 12,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      padding: 16,
      marginBottom: 12,
      transition: "all 0.2s ease",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 4,
    },
    sectionIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#1a1a1a",
    },
    sectionDescription: {
      fontSize: 12,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      marginBottom: 14,
      lineHeight: 1.5,
    },

    field: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiInputLabel-root": {
        color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: theme.palette.primary.main,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.18)"
          : "rgba(0,0,0,0.18)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.30)"
          : "rgba(0,0,0,0.30)",
      },
    },
    globalMenuPaper: {
      borderRadius: "14px !important",
      boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
      border: "none",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none",
      marginTop: 4,
      maxHeight: 360,
      ...(theme.scrollbarStyles || {}),
      "& ul": {
        padding: 6,
      },
      "& li.MuiMenuItem-root": {
        borderRadius: 8,
        padding: "9px 12px",
        marginBottom: 4,
        fontSize: 14,
        fontWeight: 500,
        color: isDark ? "#ffffff" : "#1a1a1a",
        "&:hover": {
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        },
        "&.Mui-selected": {
          background: "rgba(6, 207, 156, 0.10)",
        },
        "&.Mui-selected:hover": {
          background: "rgba(6, 207, 156, 0.15)",
        },
        "&:last-child": {
          marginBottom: 0,
        },
      },
    },

    presetWrap: {
      marginBottom: 14,
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      width: "100%",
    },
    fieldLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#111b21",
      lineHeight: 1.2,
    },

    buttonRow: {
      display: "flex",
      gap: 8,
      marginTop: 14,
      flexWrap: "wrap",
    },
    primaryButton: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 18px",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#ffffff",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      },
      "&.Mui-disabled": {
        backgroundColor: isDark
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.10)",
        color: isDark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.35)",
      },
    },
    secondaryButton: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 18px",
      background: "transparent",
      color: isDark ? "#e9edef" : "#374151",
      border: isDark
        ? "1px solid rgba(255,255,255,0.18)"
        : "1px solid rgba(0,0,0,0.18)",
      "&:hover": {
        background: isDark
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
        borderColor: isDark
          ? "rgba(255,255,255,0.30)"
          : "rgba(0,0,0,0.30)",
      },
      "&.Mui-disabled": {
        opacity: 0.4,
      },
    },

    eyeBtn: {
      color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
      padding: 4,
      "&:hover": {
        color: theme.palette.primary.main,
        background: "transparent",
      },
    },
  };
});

const SMTP_PRESETS = {
  hostinger: { label: "Hostinger", host: "smtp.hostinger.com", port: "465", secure: "true" },
  gmail: { label: "Gmail", host: "smtp.gmail.com", port: "465", secure: "true" },
  sendgrid: { label: "SendGrid", host: "smtp.sendgrid.net", port: "587", secure: "false", fixedUser: "apikey" },
  outlook: { label: "Outlook / Hotmail", host: "smtp-mail.outlook.com", port: "587", secure: "false" },
  custom: { label: i18n.t("settings.smtp.presetCustom"), host: "", port: "587", secure: "false" },
};

const SMTPConfig = ({ settings }) => {
  const classes = useStyles();
  const { update } = useCompanySettings();

  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("465");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpSecure, setSmtpSecure] = useState("true");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("custom");

  useEffect(() => {
    if (settings) {
      setSmtpHost(settings.smtpHost || "");
      setSmtpPort(settings.smtpPort || "465");
      setSmtpUser(settings.smtpUser || "");
      setSmtpPass(settings.smtpPass || "");
      setSmtpSecure(settings.smtpSecure || "true");
      setSmtpFrom(settings.smtpFrom || "");

      const host = settings.smtpHost || "";
      if (host === "smtp.hostinger.com") setSelectedPreset("hostinger");
      else if (host === "smtp.gmail.com") setSelectedPreset("gmail");
      else if (host === "smtp.sendgrid.net") setSelectedPreset("sendgrid");
      else if (host === "smtp-mail.outlook.com") setSelectedPreset("outlook");
      else if (host) setSelectedPreset("custom");
    }
  }, [settings]);

  const handlePresetChange = (e) => {
    const preset = e.target.value;
    setSelectedPreset(preset);
    if (preset !== "custom" && SMTP_PRESETS[preset]) {
      setSmtpHost(SMTP_PRESETS[preset].host);
      setSmtpPort(SMTP_PRESETS[preset].port);
      setSmtpSecure(SMTP_PRESETS[preset].secure);
      if (SMTP_PRESETS[preset].fixedUser) {
        setSmtpUser(SMTP_PRESETS[preset].fixedUser);
      }
    }
  };

  const handleSave = async () => {
    if (!smtpHost || !smtpUser || !smtpPass) {
      toast.error(i18n.t("settings.smtp.fillRequired"));
      return;
    }
    setSaving(true);
    try {
      await update({ column: "smtpHost", data: smtpHost });
      await update({ column: "smtpPort", data: smtpPort });
      await update({ column: "smtpUser", data: smtpUser });
      await update({ column: "smtpPass", data: smtpPass });
      await update({ column: "smtpSecure", data: smtpSecure });
      await update({ column: "smtpFrom", data: smtpFrom || smtpUser });
      toast.success(i18n.t("settings.smtp.saveSuccess"));
    } catch (err) {
      toast.error(i18n.t("settings.smtp.saveError"));
    }
    setSaving(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/companySettings/test-smtp");
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || i18n.t("settings.smtp.testError"));
    }
    setTesting(false);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error(i18n.t("settings.smtp.fillTestEmail"));
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await api.post("/companySettings/send-test-email", { email: testEmail });
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || i18n.t("settings.smtp.sendTestError"));
    }
    setSendingTest(false);
  };

  const menuProps = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: { className: classes.globalMenuPaper },
  };

  const tWithFallback = (key, fallback) => {
    const value = i18n.t(key);
    return value === key ? fallback : value;
  };

  const fieldHelp = (field, fallback) =>
    tWithFallback(`settings.smtp.fieldHelps.${field}`, fallback);

  const renderFieldLabel = (label, field) => (
    <span className={classes.fieldLabel}>
      {label}
      <HelpTooltipIcon
        helpKey={`settings.smtp.fieldHelpsDetailed.${field}`}
        description={fieldHelp(field, "")}
        size={13}
        placement="top"
      />
    </span>
  );

  const FieldGroup = ({ label, field, children }) => (
    <div className={classes.fieldGroup}>
      {renderFieldLabel(label, field)}
      {children}
    </div>
  );

  return (
    <div className={classes.container}>
      {/* Servidor SMTP */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionIconWrap}>
            <Mail size={18} strokeWidth={1.8} />
          </span>
          <Typography className={classes.sectionTitle}>
            {i18n.t("settings.smtp.title")}
          </Typography>
          <HelpTooltipIcon helpKey="settings.smtp.helps.serverConfig" />
        </div>
        <Typography className={classes.sectionDescription}>
          {i18n.t("settings.smtp.subtitle")}
        </Typography>

        <div className={classes.presetWrap}>
          <FieldGroup label={i18n.t("settings.smtp.preset")} field="preset">
            <FormControl variant="outlined" size="small" className={classes.field}>
            <Select
              value={selectedPreset}
              onChange={handlePresetChange}
              MenuProps={menuProps}
              inputProps={{ "aria-label": i18n.t("settings.smtp.preset") }}
            >
              {Object.entries(SMTP_PRESETS).map(([key, preset]) => (
                <MenuItem key={key} value={key}>
                  {preset.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {fieldHelp("preset", "Escolha seu provedor de e-mail. Se ele aparecer na lista, o sistema preenche servidor, porta e segurança para você. Use Personalizado quando seu provedor não estiver na lista.")}
            </FormHelperText>
          </FormControl>
          </FieldGroup>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <FieldGroup label={i18n.t("settings.smtp.host")} field="host">
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="smtp.hostinger.com"
              helperText={fieldHelp("host", "Cole aqui o endereço do servidor SMTP informado pelo seu provedor de e-mail. Exemplos: smtp.hostinger.com, smtp.gmail.com, smtp.sendgrid.net.")}
              className={classes.field}
            />
            </FieldGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldGroup label={i18n.t("settings.smtp.port")} field="port">
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              placeholder="465"
              helperText={fieldHelp("port", "Digite a porta indicada pelo provedor. Na maioria dos casos use 465 com SSL/TLS ou 587 com STARTTLS.")}
              className={classes.field}
            />
            </FieldGroup>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldGroup
              label={selectedPreset === "sendgrid" ? i18n.t("settings.smtp.sendgridUserLabel") : i18n.t("settings.smtp.user")}
              field="user"
            >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              placeholder={selectedPreset === "sendgrid" ? "apikey" : i18n.t("settings.smtp.userPlaceholder")}
              disabled={selectedPreset === "sendgrid"}
              helperText={selectedPreset === "sendgrid" ? i18n.t("settings.smtp.sendgridUserHelper") : fieldHelp("user", "Informe o login da conta de e-mail que vai enviar as mensagens. Normalmente é o próprio e-mail completo, como contato@seudominio.com.")}
              className={classes.field}
            />
            </FieldGroup>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldGroup
              label={selectedPreset === "sendgrid" ? i18n.t("settings.smtp.sendgridPasswordLabel") : i18n.t("settings.smtp.password")}
              field={selectedPreset === "sendgrid" ? "sendgridPassword" : "password"}
            >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={smtpPass}
              onChange={(e) => setSmtpPass(e.target.value)}
              helperText={selectedPreset === "sendgrid" ? fieldHelp("sendgridPassword", "Cole a API Key criada no SendGrid. Nao use a senha da sua conta SendGrid.") : fieldHelp("password", "Digite a senha SMTP da conta. Em Gmail, Outlook e outros provedores com verificacao em duas etapas, normalmente precisa ser uma senha de app, nao a senha normal de login.")}
              className={classes.field}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      className={classes.eyeBtn}
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <EyeOff size={16} strokeWidth={1.6} /> : <Eye size={16} strokeWidth={1.6} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            </FieldGroup>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldGroup label={i18n.t("settings.smtp.from")} field="from">
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={smtpFrom}
              onChange={(e) => setSmtpFrom(e.target.value)}
              placeholder={smtpUser || i18n.t("settings.smtp.fromPlaceholder")}
              helperText={selectedPreset === "sendgrid" ? i18n.t("settings.smtp.sendgridFromHelper") : fieldHelp("from", "E-mail que aparecerá como remetente para quem receber a mensagem. Se deixar vazio, o sistema usa o Usuário / Email acima.")}
              className={classes.field}
            />
            </FieldGroup>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldGroup label={i18n.t("settings.smtp.secure")} field="secure">
            <FormControl variant="outlined" size="small" className={classes.field}>
              <Select
                value={smtpSecure}
                onChange={(e) => setSmtpSecure(e.target.value)}
                MenuProps={menuProps}
                inputProps={{ "aria-label": i18n.t("settings.smtp.secure") }}
              >
                <MenuItem value="true">{i18n.t("settings.smtp.secureSslOption")}</MenuItem>
                <MenuItem value="false">{i18n.t("settings.smtp.secureStarttlsOption")}</MenuItem>
              </Select>
              <FormHelperText>
                {fieldHelp("secure", "Escolha o tipo de seguranca que combina com a porta. Porta 465 costuma usar SSL/TLS. Porta 587 costuma usar STARTTLS.")}
              </FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>
        </Grid>

        <div className={classes.buttonRow}>
          <Button
            variant="contained"
            className={classes.primaryButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={20} style={{ color: "inherit" }} />
            ) : (
              i18n.t("settings.smtp.save")
            )}
          </Button>
          <Button
            variant="outlined"
            className={classes.secondaryButton}
            onClick={handleTestConnection}
            disabled={testing || !smtpHost}
            startIcon={<Plug size={14} strokeWidth={1.8} />}
          >
            {testing ? (
              <CircularProgress size={20} style={{ color: "inherit" }} />
            ) : (
              i18n.t("settings.smtp.testConnection")
            )}
          </Button>
        </div>
      </div>

      {/* Enviar e-mail de teste */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionIconWrap}>
            <Send size={18} strokeWidth={1.8} />
          </span>
          <Typography className={classes.sectionTitle}>
            {i18n.t("settings.smtp.sendTestTitle")}
          </Typography>
          <HelpTooltipIcon helpKey="settings.smtp.helps.sendTest" />
        </div>
        <Typography className={classes.sectionDescription}>
          {i18n.t("settings.smtp.sendTestSubtitle")}
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={8}>
            <FieldGroup label={i18n.t("settings.smtp.testEmailLabel")} field="testEmail">
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder={i18n.t("settings.smtp.testEmailPlaceholder")}
              helperText={fieldHelp("testEmail", "Digite um e-mail seu ou de alguem da equipe para receber o teste. Assim voce confirma se o envio esta funcionando.")}
              className={classes.field}
            />
            </FieldGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={handleSendTestEmail}
              disabled={sendingTest || !testEmail}
              fullWidth
              startIcon={!sendingTest && <Send size={14} strokeWidth={1.8} />}
              style={{ minHeight: 40 }}
            >
              {sendingTest ? (
                <CircularProgress size={20} style={{ color: "inherit" }} />
              ) : (
                i18n.t("settings.smtp.sendTest")
              )}
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default SMTPConfig;
